import * as XLSX from 'xlsx'
import { BulkImportWizardParsedSheet } from '@betterboards/shared/types/Import'

export interface ParseFileResult {
  sheets: BulkImportWizardParsedSheet[]
  workbook: XLSX.WorkBook
  detectedHeaderRows: Record<string, number>
}

const HEADER_TAG_PATTERN = /\$header\s*=\s*(\d+)/i
const IGNORE_TAG_PATTERN = /\$(ignore|skip)/i

/**
 * Check if a sheet is hidden in the workbook.
 * Hidden state: 0 = visible, 1 = hidden, 2 = very hidden
 */
export function isSheetHidden (workbook: XLSX.WorkBook, sheetName: string): boolean {
  const sheetIndex = workbook.SheetNames.indexOf(sheetName)
  if (sheetIndex === -1) {
    return false
  }
  const sheetInfo = workbook.Workbook?.Sheets?.[sheetIndex]
  return sheetInfo?.Hidden !== undefined && sheetInfo.Hidden !== 0
}

/**
 * Check if a sheet has an $ignore or $skip tag in the first few cells.
 */
export function hasIgnoreTag (worksheet: XLSX.WorkSheet): boolean {
  const range = worksheet['!ref'] ? XLSX.utils.decode_range(worksheet['!ref']) : null
  if (!range) {
    return false
  }

  const maxRowsToScan = Math.min(10, range.e.r + 1)
  const maxColsToScan = Math.min(10, range.e.c + 1)

  for (let row = 0; row < maxRowsToScan; row++) {
    for (let col = 0; col < maxColsToScan; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col })
      const cell = worksheet[cellAddress]
      if (cell && typeof cell.v === 'string' && IGNORE_TAG_PATTERN.test(cell.v)) {
        return true
      }
    }
  }

  return false
}

/**
 * Detect a hidden $header=N tag in the first few rows/columns of a worksheet.
 * Returns the 0-indexed header row, or 0 if no tag is found.
 */
export function detectHeaderRowTag (worksheet: XLSX.WorkSheet): number {
  const range = worksheet['!ref'] ? XLSX.utils.decode_range(worksheet['!ref']) : null
  if (!range) {
    return 0
  }

  const maxRowsToScan = Math.min(10, range.e.r + 1)
  const maxColsToScan = Math.min(10, range.e.c + 1)

  for (let row = 0; row < maxRowsToScan; row++) {
    for (let col = 0; col < maxColsToScan; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col })
      const cell = worksheet[cellAddress]
      if (cell && typeof cell.v === 'string') {
        const match = cell.v.match(HEADER_TAG_PATTERN)
        if (match) {
          const tagValue = parseInt(match[1], 10)
          // Tag value is 1-indexed (user-friendly), convert to 0-indexed
          return Math.max(0, tagValue - 1)
        }
      }
    }
  }

  return 0
}

/**
 * Extract column names from the header row, filtering out empty cells
 */
function extractHeaderColumns (worksheet: XLSX.WorkSheet, headerRow: number): string[] {
  const rawData = XLSX.utils.sheet_to_json<string[]>(worksheet, {
    range: headerRow,
    header: 1
  })

  if (rawData.length === 0) {
    return []
  }

  const headerCells = rawData[0]
  const columns: string[] = []

  for (const cell of headerCells) {
    if (typeof cell === 'string' && cell.trim() !== '') {
      columns.push(cell)
    }
  }

  return columns
}

/**
 * Parse a single worksheet with a specific header row.
 * Uses explicit column headers to ensure rows and columns stay consistent.
 */
export function parseWorksheet (
  workbook: XLSX.WorkBook,
  sheetName: string,
  headerRow: number = 0
): BulkImportWizardParsedSheet {
  const worksheet = workbook.Sheets[sheetName]
  const columns = extractHeaderColumns(worksheet, headerRow)
  const rows = columns.length > 0
    ? XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, { range: headerRow + 1, header: columns })
    : XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, { range: headerRow })

  return {
    name: sheetName,
    columns,
    rows
  }
}
